import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Skeleton, Table, TableProps } from "antd";
import { FaEdit, FaEye, FaTrashAlt } from "react-icons/fa";
import axiosInstance from "../../../utility/axiosInstance";
import { BiSearchAlt } from "react-icons/bi";
import SearchInput from "../../../components/elements/forms/SearchInput";
import { IJobFetch } from "../../../store/types/job";
import { delete_job } from "../../../services/api/job";
import { useAppDispatch } from "../../../store/hooks";
import DeleteConfirmModal from "../../../components/elements/DeleteConfirmModalJob";
import JobDetailsModal from "../../../components/elements/JobDetailsModal";

type ApiResponse = {
  message: string;
  data: IJobFetch[];
};

const JobsCreated: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<IJobFetch[]>([]);
  const [loading, setLoading] = useState(true);
  const [filteredJobs, setFilteredJobs] = useState<IJobFetch[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  // const [showMore, setShowMore] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    job: null as IJobFetch | null,
  });
  const [detailsModal, setDetailsModal] = useState({
    isOpen: false,
    job: null as IJobFetch | null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const getJobs = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get<ApiResponse>("/job");
      // setJobs(response.data.data);
      // setFilteredJobs(response.data.data); // Initialize filtered data with all jobs
      const sortedJobs = response.data.data.sort(
        (a: IJobFetch, b: IJobFetch) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setJobs(sortedJobs);
      setFilteredJobs(sortedJobs); // Initialize filtered data with all jobs
    } catch (err) {
      console.error("Error fetching jobs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getJobs();
  }, []);

  // Filter jobs based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredJobs(jobs);
    } else {
      const filtered = jobs.filter(
        (job) =>
          job.currentTemplate.title
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
        // Add other fields to search if needed:
        // || job.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredJobs(filtered);
    }
  }, [searchTerm, jobs]);

  const onLoad = React.useCallback(() => {
    getJobs();
  }, []);

  const onScroll = React.useCallback(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    onLoad();
    onScroll();
  }, [onLoad, onScroll]);

  React.useEffect(() => {
    console.log("Jobs data:", jobs);
  }, [jobs]);

  // const maxChar = 80;

  const columns: TableProps<IJobFetch>["columns"] = [
    {
      title: "S/N",
      dataIndex: "key",
      width: 20,
      align: "center",
      render: (_, __, index) => index + 1,
      // rowScope: 'row',
    },
    {
      title: "Job Title",
      dataIndex: "title",
      render: (_, record) => (
        <span
          className="cursor-pointer text-black"
          onClick={() => setDetailsModal({ isOpen: true, job: record })}
        >
          {record.currentTemplate.title}
        </span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (_, record) => (
        <span
          className="cursor-pointer text-black"
          onClick={() => setDetailsModal({ isOpen: true, job: record })}
        >
          {`${record.currentTemplate.description.substring(0, 65) + "..."}`}
        </span>
      ),
      // render: (_, record) => record.description.substring(0, maxChar) + "...",
    },
    {
      title: "Questions",
      dataIndex: "interviewTemplate",
      key: "interviewQuestions",
      render: (_, record) => {
        const questions =
          record.currentTemplate.interviewQuestions?.length > 0
            ? record.currentTemplate.interviewQuestions
            : record.currentTemplate.questions?.map(
                (q: any) => q.question || q,
              ) || [];

        const questionsText = questions.join("- ");

        return (
          <span
            className="cursor-pointer text-black"
            onClick={() => setDetailsModal({ isOpen: true, job: record })}
          >
            {questionsText.length > 0
              ? `${questionsText.substring(0, 65)}...`
              : "No questions"}
          </span>
        );
      },
    },

    {
      title: "Interview Mode",
      dataIndex: "interviewMode",
      render: (_, record) => (
        <span className="text-black">
          {record.currentTemplate.interviewMode
            ?.replace(/_+/g, " ")
            .toLowerCase()
            ?.replace(/\b\w/g, (char) => char.toUpperCase())}
        </span>
      ),
    },
    {
      title: "Pretest/Ass.",
      dataIndex: "pretestId",
      render: (_, record) => (
        <div className="text-black">
          {record.currentTemplate.pretestId === null ? (
            <div>No</div>
          ) : (
            <p>Yes</p>
          )}
        </div>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "createdAt",
      render: (date) => (
        <span className="text-black">
          {new Date(date).toLocaleDateString()}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      // width: 180,
      align: "center",
      render: (_, record) => (
        <div className="flex items-center justify-center">
          <Link to={`/interviews/${record.id}`} className="cursor-pointer">
            <FaEye className="text-[green] text-[18px]" />
          </Link>
          <div className="w-[12px]"></div>
          <Link to={`/edit-job/${record.id}`} className="cursor-pointer">
            {/* Edit */}
            <FaEdit className="text-[18px] text-primary" />
          </Link>
          <div className="w-[12px]"></div>
          <div
            onClick={() => {
              console.log("Delete button clicked for:", record);
              setDeleteModal({
                isOpen: true,
                job: record,
              });
              // delete_job(dispatch, record.id);
            }}
            className="cursor-pointer"
          >
            <FaTrashAlt className="text-[red] text-[18px]" />
          </div>
        </div>
      ),
    },
  ];

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (deleteModal.job) {
      console.log("Confirmed delete for:", deleteModal.job);
      setIsDeleting(true);
      try {
        await delete_job(dispatch, deleteModal.job.id, () => {
          // Refetch the data to show updated list
          getJobs();
          // Close the modal
          setDeleteModal({ isOpen: false, job: null });
        });
      } catch (error) {
        // Error is already handled in the API call
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    console.log("Delete cancelled");
    setDeleteModal({ isOpen: false, job: null });
  };
  // const tableData = React.useMemo(() => {
  //   if (!jobs || !Array.isArray(jobs)) return [];

  //   return jobs.map((job) => ({
  //     id: job.id,
  //     title: job.title,
  //     description: job.description,
  //     createdAt: new Date(job.createdAt).toLocaleDateString(),
  //     actions: "View Details",
  //   }));
  // }, [jobs]);

  // const handleRowClick = (row: IJobCreation) => {
  //   alert(`Clicked row: ${row.title}`);
  // };
  return (
    <div className="w-full py-5 font-abel">
      <div className="max-w-[1096px] mx-auto px-3">
        <div className="mt-2 flex items-center justify-between">
          <p className="font-[600]">Newly Created Jobs</p>
          <SearchInput
            className="w-full"
            name="search"
            value={searchTerm}
            placeholder="Search here..."
            icon={<BiSearchAlt size={18} color="#243677" />}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div
            className="h-[40px] w-[150px] flex items-center justify-center bg-secondary text-white rounded-8 ml-3 cursor-pointer"
            onClick={() => navigate(`/create-job`)}
          >
            Add New Job
          </div>
        </div>

        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : jobs ? (
          <Table
            dataSource={filteredJobs}
            // dataSource={jobs}
            columns={columns}
            loading={loading}
            key="id"
            rowKey="id"
          />
        ) : (
          <div className="text-center text-gray-500">
            Contents failed to load, please try again.
          </div>
        )}

        <DeleteConfirmModal
          job={deleteModal.job}
          isOpen={deleteModal.isOpen}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          loading={isDeleting}
        />

        <JobDetailsModal
          isOpen={detailsModal.isOpen}
          onClose={() => setDetailsModal({ isOpen: false, job: null })}
          job={detailsModal.job}
        />
      </div>
    </div>
  );
};

export default JobsCreated;
